import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FileText, Send, CheckCircle, Lock, Clock, PlaneTakeoff, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { templatesAPI, airportAnnouncementsAPI, gatesAPI, gateQueueAPI } from '../../services/api';
import { useMyActiveGateSession } from '../../hooks/useMyActiveGateSession';
import { useGateFlightContext } from '../../hooks/useGateFlightContext';
import { knownPlaceholderValues, fillEmpty, isAirportTemplate } from '../../utils/templatePrefill';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/UI/PageHeader';
import Button from '../../components/UI/Button';

// CR 8.1 — request an admin-approved template, fill its variables, choose a
// playback-timing method, and submit for approval (never plays automatically).
const PREVIEW_DEBOUNCE_MS = 400;

const TIMING_OPTIONS = [
  { value: 'relative', label: 'In N minutes' },
  { value: 'clock', label: 'At a specific time' },
  { value: 'sequence', label: 'After another announcement' },
];

export default function RequestTemplatePage() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  // Manager/Supervisor may request for any gate; an Operator may only request for
  // the gate they're currently working (CR 4.1) — sourced from their own active
  // session, never the URL param, so it can't be spoofed; backend enforces this too.
  const isOperator = user?.role === 'gate_operator';
  const { session: mySession } = useMyActiveGateSession();
  // Same convenience default as the Ad-Hoc tab for non-operator roles: arriving with
  // ?gate_id= (from an in-session link) pre-fills the picker, but stays editable.
  const paramGateId = searchParams.get('gate_id') || '';
  const lockedGateId = isOperator ? (mySession ? String(mySession.gate_id) : '') : paramGateId;

  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [values, setValues] = useState({});
  const [preview, setPreview] = useState('');
  const [gates, setGates] = useState([]);
  const [gateId, setGateId] = useState(lockedGateId);
  const [sending, setSending] = useState(false);
  const [lastSent, setLastSent] = useState(null);

  useEffect(() => {
    if (lockedGateId) setGateId(lockedGateId);
  }, [lockedGateId]);

  const [timingType, setTimingType] = useState('relative');
  const [relativeMinutes, setRelativeMinutes] = useState(5);
  const [scheduledAt, setScheduledAt] = useState('');
  const [insertAfterId, setInsertAfterId] = useState('');
  const [upcoming, setUpcoming] = useState([]);

  const template = useMemo(() => templates.find(t => String(t.id) === String(templateId)), [templates, templateId]);
  // The stored `placeholders` column is normally kept in sync with
  // template_text by the admin create/update flow, but it can drift out of
  // sync for a template inserted another way (e.g. a seed script that wrote
  // template_text directly) — fall back to deriving them live from the text
  // itself rather than silently rendering an empty fill-in form, which would
  // leave every {Placeholder} unfilled and spoken by TTS as literal text.
  const placeholders = useMemo(() => {
    if (!template) return [];
    if (template.placeholders?.length > 0) return template.placeholders;
    const matches = template.template_text?.match(/\{([^}]+)\}/g) || [];
    return [...new Set(matches.map(m => m.slice(1, -1)))];
  }, [template]);

  useEffect(() => {
    templatesAPI.getAll({ isActive: true, limit: 100 })
      .then(res => setTemplates((res.data.data?.templates || res.data.data || []).filter(isAirportTemplate)))
      .catch(() => {});
    gatesAPI.getAll()
      .then(res => setGates(res.data.data || []))
      .catch(() => {});
  }, []);

  // Pre-fill what we already know about this gate's flight ({FlightNumber},
  // {Gate}, {Destination}, ...). A new template starts fresh; a late-arriving
  // flight lookup only fills fields still empty, never overwriting user input.
  const flightCtx = useGateFlightContext(gateId, isOperator ? mySession?.id : undefined);
  const prefillTemplateRef = useRef(null);
  useEffect(() => {
    const known = knownPlaceholderValues(placeholders, flightCtx);
    if (prefillTemplateRef.current !== templateId) {
      prefillTemplateRef.current = templateId;
      setValues(known);
      setPreview('');
    } else {
      setValues(v => fillEmpty(v, known));
    }
  }, [templateId, placeholders, flightCtx]);

  useEffect(() => {
    if (!templateId) return;
    // Debounced — previewing on every keystroke burned through the API rate
    // limit and stacked "Too many requests" toasts. Preview is best-effort, so
    // it never toasts; a stale response is dropped if the user kept typing.
    let stale = false;
    const timer = setTimeout(() => {
      templatesAPI.preview(templateId, values, { skipErrorToast: true })
        .then(res => { if (!stale) setPreview(res.data.data?.filledText || ''); })
        .catch(() => {});
    }, PREVIEW_DEBOUNCE_MS);
    return () => { stale = true; clearTimeout(timer); };
  }, [templateId, values]);

  useEffect(() => {
    if (!gateId || timingType !== 'sequence') return;
    gateQueueAPI.getQueue(gateId)
      .then(res => setUpcoming(res.data.data?.upcoming || []))
      .catch(() => setUpcoming([]));
  }, [gateId, timingType]);

  async function handleSubmit() {
    if (!template) {
      toast.error('Please select a template');
      return;
    }
    if (!gateId) {
      toast.error('Please select a gate');
      return;
    }
    if (timingType === 'clock' && !scheduledAt) {
      toast.error('Please choose a playback time');
      return;
    }
    const missing = placeholders.filter(p => !values[p]?.trim());
    if (missing.length > 0) {
      toast.error(`Fill in: ${missing.join(', ')}`);
      return;
    }
    try {
      setSending(true);
      await airportAnnouncementsAPI.submit({
        announcement_type: 'zone_announcement',
        title: `${template.name} — ${new Date().toLocaleDateString()}`,
        template_id: template.id,
        template_variables: values,
        suggested_gate_id: gateId,
        timing_type: timingType,
        relative_minutes: timingType === 'relative' ? Number(relativeMinutes) || 0 : undefined,
        scheduled_at: timingType === 'clock' ? new Date(scheduledAt).toISOString() : undefined,
        insert_after_id: timingType === 'sequence' ? (insertAfterId || null) : undefined,
      });
      setLastSent({ text: preview, time: new Date() });
      setTemplateId('');
      toast.success('Submitted — a supervisor or manager will review it before it plays');
    } catch (err) {
      if (!err._toasted) toast.error(err.response?.data?.message || 'Failed to submit request');
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="px-4 py-5 max-w-lg mx-auto">
      <PageHeader
        icon={FileText}
        title="Request Template Announcement"
        subtitle="Pick an approved template — a supervisor or manager reviews it before it plays"
      />

      {/* Gate selector — an Operator is locked to the gate they're currently working;
          Supervisor/Manager/Admin may choose any gate. */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Gate *</label>
        {isOperator ? (
          mySession ? (
            <div className="w-full border border-gray-200 rounded-xl px-3 py-3 text-sm bg-gray-50 flex items-center justify-between text-gray-700 font-medium">
              <div className="flex items-center gap-2">
                <span>{mySession.gate?.gate_code || `Gate #${mySession.gate_id}`}</span>
                {mySession.flight_number && (
                  <span className="flex items-center gap-1 text-xs text-gray-500 font-normal">
                    <PlaneTakeoff size={11} className="text-gray-400" />
                    {mySession.flight_number}
                  </span>
                )}
              </div>
              <Lock size={13} className="text-gray-400" />
            </div>
          ) : (
            <div className="w-full border border-amber-200 rounded-xl px-3 py-3 text-sm bg-amber-50 flex items-center gap-2 text-amber-700">
              <AlertTriangle size={15} className="flex-shrink-0" />
              You need an active gate session before requesting a template announcement.
            </div>
          )
        ) : (
          <select
            value={gateId}
            onChange={e => setGateId(e.target.value)}
            className="w-full border border-gray-300 rounded-xl px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select gate</option>
            {gates.map(g => (
              <option key={g.id} value={g.id}>
                {g.gate_code}{g.terminal ? ` · ${g.terminal}` : ''}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Template picker */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Template *</label>
        <select
          value={templateId}
          onChange={e => setTemplateId(e.target.value)}
          className="w-full border border-gray-300 rounded-xl px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Select a template</option>
          {templates.map(t => (
            <option key={t.id} value={t.id}>{t.name} ({t.category})</option>
          ))}
        </select>
      </div>

      {/* Placeholder fields */}
      {placeholders.length > 0 && (
        <div className="mb-4 space-y-3">
          {placeholders.map(p => (
            <div key={p}>
              <label className="block text-xs font-medium text-gray-600 mb-1">{p}</label>
              <input
                type="text"
                value={values[p] || ''}
                onChange={e => setValues(v => ({ ...v, [p]: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          ))}
        </div>
      )}

      {/* Live preview */}
      {template && (
        <div className="mb-5 bg-gray-50 border border-gray-200 rounded-xl p-3">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Preview</p>
          <p className="text-sm text-gray-800">{preview || template.template_text}</p>
        </div>
      )}

      {/* Playback timing (CR 8.3) */}
      <div className="mb-5">
        <label className="block text-sm font-medium text-gray-700 mb-1">When should this play?</label>
        <div className="flex gap-2 mb-2">
          {TIMING_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setTimingType(opt.value)}
              className={`flex-1 px-2 py-2 rounded-xl text-xs font-medium border transition-colors ${
                timingType === opt.value
                  ? 'bg-blue-700 text-white border-blue-700'
                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {timingType === 'relative' && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">In</span>
            <input
              type="number"
              min={0}
              value={relativeMinutes}
              onChange={e => setRelativeMinutes(e.target.value)}
              className="w-20 border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-600">minute(s) — 0 means as soon as possible</span>
          </div>
        )}

        {timingType === 'clock' && (
          <input
            type="datetime-local"
            value={scheduledAt}
            onChange={e => setScheduledAt(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        )}

        {timingType === 'sequence' && (
          <select
            value={insertAfterId}
            onChange={e => setInsertAfterId(e.target.value)}
            disabled={!gateId}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
          >
            <option value="">Play first, before anything queued</option>
            {upcoming.map(a => (
              <option key={a.id} value={a.id}>
                After: {(a.english_text || a.announcement_type || '').slice(0, 60)}
              </option>
            ))}
          </select>
        )}
      </div>

      <Button
        variant="primary"
        size="xl"
        fullWidth
        icon={Send}
        loading={sending}
        disabled={!template || !gateId}
        onClick={handleSubmit}
        className="shadow"
      >
        {sending ? 'Submitting…' : 'Submit for Approval'}
      </Button>

      {lastSent && (
        <div className="mt-5 bg-yellow-50 border border-yellow-200 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle size={16} className="text-yellow-600" />
            <span className="text-sm font-semibold text-yellow-800">Submitted — awaiting approval</span>
            <span className="text-xs text-yellow-600 ml-auto flex items-center gap-1">
              <Clock size={11} />
              {lastSent.time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
          <p className="text-xs text-yellow-700 line-clamp-2">{lastSent.text}</p>
        </div>
      )}
    </div>
  );
}
